/**
 * Landing page with interactive map and login - Gold Standard Design
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ChevronRight, Play, Users, Building2, Globe, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MexicoMap } from '../components/MexicoMap';
import { clubsAPI } from '../services/api';
import { useAuthStore } from '../store';
import { useNavigate } from 'react-router-dom';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [cities, setCities] = useState([]);
  const [stats, setStats] = useState({
    total_clubs: 0,
    total_courts: 0,
    total_cities: 0,
    total_countries: 0
  });
  const [selectedCity, setSelectedCity] = useState(null);
  const [showInfo, setShowInfo] = useState(true);
  
  useEffect(() => {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated) {
      navigate('/home');
      return;
    }
    
    // Fetch cities and stats in parallel
    const fetchData = async () => {
      try {
        const [citiesRes, statsRes] = await Promise.all([
          clubsAPI.getCities(),
          clubsAPI.getPublicStats()
        ]);
        
        setCities(citiesRes.data.cities || []);
        setStats(statsRes.data || {
          total_clubs: 0,
          total_courts: 0,
          total_cities: 0,
          total_countries: 0
        });
      } catch (err) {
        console.error('Failed to fetch data:', err);
        // Use fallback data
        setCities([]);
        setStats({
          total_clubs: 20,
          total_courts: 40,
          total_cities: 15,
          total_countries: 2
        });
      }
    };
    
    fetchData();
  }, [isAuthenticated, navigate]);
  
  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#070A0F]">
      {/* Map background */}
      <div className="absolute inset-0 z-0">
        <MexicoMap
          cities={cities}
          selectedCity={selectedCity}
          onCityClick={(city) => setSelectedCity(city)}
        />
      </div>
      
      {/* Top header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        className="absolute top-0 left-0 right-0 z-10 p-4 sm:p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#BBFF00] flex items-center justify-center shadow-[0_0_20px_rgba(187,255,0,0.35)]">
              <Play className="w-5 h-5 text-[#070A0F] fill-current" />
            </div>
            <div>
              <span className="font-display font-bold text-xl tracking-tight text-slate-50">Clipper</span>
              <p className="text-xs text-slate-500 font-mono tracking-wider">SPORTS REPLAY</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#131B2A] border border-white/10">
            <Users className="w-4 h-4 text-[#BBFF00]" />
            <span className="text-sm text-slate-300 font-medium">{stats.total_clubs} clubes</span>
          </div>
        </div>
      </motion.header>
      
      {/* Login panel - centered on desktop, bottom on mobile */}
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ delay: 0.3, duration: 0.35 }}
            className="absolute inset-0 z-10 flex items-center justify-center p-4 md:p-8"
          >
            <div className="w-full max-w-md mt-auto md:mt-0 mb-8 md:mb-0">
              <div className="glass-elevated rounded-3xl p-6 md:p-8">
                {/* Selected city info */}
                {selectedCity ? (
                  <motion.div
                    key={selectedCity.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-6"
                  >
                    <div className="flex items-center gap-2 text-[#BBFF00] mb-2">
                      <MapPin className="w-5 h-5" />
                      <h2 className="font-display font-bold text-xl">{selectedCity.name}</h2>
                      {selectedCity.country && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#BBFF00]/20 text-[#BBFF00]">
                          {selectedCity.country === 'USA' ? '🇺🇸' : '🇲🇽'}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400">
                      {selectedCity.clubs} {selectedCity.clubs === 1 ? 'club' : 'clubes'} · {selectedCity.courts || 0} canchas
                    </p>
                  </motion.div>
                ) : (
                  <div className="mb-6 text-center md:text-left">
                    <h2 className="font-display font-bold text-2xl md:text-3xl mb-3 text-slate-50 leading-tight">
                      Captura tus mejores jugadas
                    </h2>
                    <p className="text-slate-400 font-mono text-sm tracking-wider uppercase">
                      Sports Replay App
                    </p>
                  </div>
                )}
                
                {/* CTA Buttons */}
                <div className="space-y-3">
                  <Button
                    className="w-full h-14 text-lg font-bold uppercase tracking-wider bg-[#BBFF00] text-[#070A0F] hover:bg-[#A2DC00] shadow-[0_0_25px_rgba(187,255,0,0.35)] active:scale-[0.97] transition-all duration-200"
                    onClick={() => navigate('/auth')}
                    data-testid="login-btn"
                  >
                    Iniciar sesión
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                  
                  <Button
                    variant="secondary"
                    className="w-full h-12 bg-[#0D121D] hover:bg-[#131B2A] border border-white/10 hover:border-[#BBFF00]/40 text-slate-200 hover:text-[#BBFF00] transition-all duration-200"
                    onClick={() => navigate('/scan')}
                    data-testid="scan-qr-btn"
                  >
                    Escanear QR de cancha
                  </Button>
                </div>
                
                {/* Dynamic stats from backend */}
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="flex flex-col items-center p-2 rounded-xl bg-[#0D121D]/50">
                      <div className="flex items-center gap-1 text-[#BBFF00]">
                        <LayoutGrid className="w-3.5 h-3.5" />
                        <span className="font-display font-bold text-lg">{stats.total_courts}</span>
                      </div>
                      <span className="text-xs text-slate-500 font-mono tracking-wider">CANCHAS</span>
                    </div>
                    <div className="flex flex-col items-center p-2 rounded-xl bg-[#0D121D]/50">
                      <div className="flex items-center gap-1 text-[#BBFF00]">
                        <Building2 className="w-3.5 h-3.5" />
                        <span className="font-display font-bold text-lg">{stats.total_clubs}</span>
                      </div>
                      <span className="text-xs text-slate-500 font-mono tracking-wider">CLUBES</span>
                    </div>
                    <div className="flex flex-col items-center p-2 rounded-xl bg-[#0D121D]/50">
                      <div className="flex items-center gap-1 text-[#BBFF00]">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="font-display font-bold text-lg">{stats.total_cities}</span>
                      </div>
                      <span className="text-xs text-slate-500 font-mono tracking-wider">CIUDADES</span>
                    </div>
                    <div className="flex flex-col items-center p-2 rounded-xl bg-[#0D121D]/50">
                      <div className="flex items-center gap-1 text-[#BBFF00]">
                        <Globe className="w-3.5 h-3.5" />
                        <span className="font-display font-bold text-lg">{stats.total_countries}</span>
                      </div>
                      <span className="text-xs text-slate-500 font-mono tracking-wider">PAÍSES</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
